import React, { useState, useEffect, useContext } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

import { AuthContext } from "../../contexts/auth";
import postStripeSuccess from "../../services/stripe/postStripeSuccess";

const PaymentSuccess = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setUser } = useContext(AuthContext);
    const [status, setStatus] = useState('loading');
    const [subscriptionName, setSubscriptionName] = useState('');

    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        
        if (!sessionId) {
            setStatus('error');
            return;
        }

        const verifyPayment = async () => {
            try {
                const result = await postStripeSuccess(sessionId);
                if (result.success) {
                    setStatus('success');
                    setSubscriptionName(result.subscription);
                    toast.success('Your subscription has been activated!');
                } else {
                    setStatus('error');
                }
            } catch (error) {
                console.error('Payment verification error:', error);
                setStatus('error');
                toast.error('There was an issue verifying your payment. Please contact support.');
            }
        };

        verifyPayment();
    }, [searchParams]);

    const handleGoToDashboard = () => {
        navigate('/dashboard');
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            padding: '20px',
        }}>
            <Card sx={{
                maxWidth: 500,
                width: '100%',
                borderRadius: '16px',
                textAlign: 'center',
                padding: '40px 20px',
            }}>
                <CardContent>
                    {status === 'loading' && (
                        <>
                            <CircularProgress size={60} sx={{ color: '#702180', mb: 3 }} />
                            <h2 style={{ margin: '0 0 16px 0', color: '#333' }}>
                                Verifying your payment...
                            </h2>
                            <p style={{ color: '#666', margin: 0 }}>
                                Please wait while we confirm your subscription.
                            </p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <CheckCircleIcon sx={{ fontSize: 80, color: '#00a271', mb: 2 }} />
                            <h2 style={{ margin: '0 0 16px 0', color: '#333' }}>
                                Payment Successful!
                            </h2>
                            <p style={{ color: '#666', margin: '0 0 8px 0' }}>
                                Welcome to <strong>{subscriptionName}</strong>!
                            </p>
                            <p style={{ color: '#666', margin: '0 0 32px 0' }}>
                                Your subscription is now active and you have access to all premium features.
                            </p>
                            <Button
                                variant="contained"
                                onClick={handleGoToDashboard}
                                sx={{
                                    backgroundColor: '#00a271',
                                    color: 'white',
                                    padding: '12px 40px',
                                    borderRadius: '12px',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#008f63',
                                    }
                                }}
                            >
                                Go to Dashboard
                            </Button>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <ErrorIcon sx={{ fontSize: 80, color: '#ff6c6c', mb: 2 }} />
                            <h2 style={{ margin: '0 0 16px 0', color: '#333' }}>
                                Something went wrong
                            </h2>
                            <p style={{ color: '#666', margin: '0 0 32px 0' }}>
                                We couldn't verify your payment. If you were charged, please contact our support team.
                            </p>
                            <Button
                                variant="contained"
                                onClick={() => navigate('/pricing')}
                                sx={{
                                    backgroundColor: '#702180',
                                    color: 'white',
                                    padding: '12px 40px',
                                    borderRadius: '12px',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#5a1a66',
                                    }
                                }}
                            >
                                Back to Pricing
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default PaymentSuccess;
